import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../utils/baseQueryWithAuth';

export type BannerStyle = 'info' | 'success' | 'warning' | 'festive' | 'love' | 'premium';
export type RuleType = 'percent_off' | 'nth_free';
export type AudienceType = 'all_clients' | 'selected_clients';

export interface PromotionAudience {
  audience_type: AudienceType;
  clients: number[];
}

export interface PromotionEligibility {
  apply_to_all_services: boolean;
  categories: number[];
  services: number[];
}

export interface PromotionRule {
  rule_type: RuleType;
  params: Record<string, any>;
}

export interface Promotion {
  promotion_id: number;
  salon: number;
  group_id?: string | null;
  name: string;
  description?: string;
  is_active: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  is_stackable: boolean;
  priority: number;
  max_uses_total?: number | null;
  max_uses_per_client?: number | null;
  banner_enabled: boolean;
  banner_message: string;
  banner_style: BannerStyle;
  banner_priority: number;
  audience?: PromotionAudience;
  eligibility?: PromotionEligibility;
  rule?: PromotionRule;
  created_at?: string;
  updated_at?: string;
}

export type PromotionCreateRequest = Omit<Promotion, 'promotion_id' | 'created_at' | 'updated_at'>;
export type PromotionUpdateRequest = Partial<PromotionCreateRequest>;

export type PromotionApplyToGroupResponse = {
  updated_promotion_ids: number[];
  errors: Array<{ promotion_id: number; errors: any }>;
};

export const promotionsApi = createApi({
  reducerPath: 'promotionsApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Promotions'],
  endpoints: (builder) => ({
    promotionsList: builder.query<Promotion[], void>({
      query: () => ({ url: `/api/promotions/` }),
      providesTags: ['Promotions'],
    }),

    promotionsCreate: builder.mutation<Promotion, PromotionCreateRequest>({
      query: (body) => ({
        url: `/api/promotions/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Promotions'],
    }),

    promotionsPartialUpdate: builder.mutation<Promotion, { promotionId: number; body: PromotionUpdateRequest }>({
      query: ({ promotionId, body }) => ({
        url: `/api/promotions/${promotionId}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Promotions'],
    }),

    promotionsApplyToGroup: builder.mutation<PromotionApplyToGroupResponse, { promotionId: number; body: Record<string, any> }>({
      query: ({ promotionId, body }) => ({
        url: `/api/promotions/${promotionId}/apply-to-group/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Promotions'],
    }),

    promotionsDestroy: builder.mutation<void, { promotionId: number }>({
      query: ({ promotionId }) => ({
        url: `/api/promotions/${promotionId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Promotions'],
    }),
  }),
});

export const {
  usePromotionsListQuery,
  usePromotionsCreateMutation,
  usePromotionsPartialUpdateMutation,
  usePromotionsApplyToGroupMutation,
  usePromotionsDestroyMutation,
} = promotionsApi;
